import { useState } from 'react';
import { Menu, X, User, Shield, ChevronDown } from 'lucide-react';
import { SignInButton } from './auth';
import { useUser, useClerk } from '@clerk/clerk-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ThemeToggle } from './ThemeToggle';
import { useAdmin } from '../lib/hooks';
import { useCache } from './CacheContext';
import { NotificationBell } from './NotificationBell';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { isSignedIn } = useUser();
  const { isAdmin } = useAdmin();
  const clerk = useClerk();
  const navigate = useNavigate();
  const location = useLocation();
  const { clearCache } = useCache();

  const handleProfileClick = () => {
    if (isSignedIn) {
      navigate('/user');
    } else {
      clerk.openSignIn();
    }
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-white dark:bg-slate-900 shadow-lg dark:shadow-none border-b-2 border-b-[#0EA5E9] transition-colors duration-300">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">

          {/* Logo */}
          <div
            className="flex items-center gap-2 cursor-pointer group hover:opacity-80 transition-opacity"
            onClick={() => {
              if (location.pathname === '/') {
                window.location.reload();
              } else {
                navigate('/');
              }
            }}
          >
            <div className="relative">
              {/* Logo modo claro */}
              <img
                src="/assets/logoEscalableHost200x50.svg"
                alt="Escalable Host"
                className="h-10 w-auto dark:hidden transition-all duration-300"
              />
              {/* Logo modo oscuro */}
              <img
                src="/assets/logoEscalableHost-ModoOscuro200x50.svg"
                alt="Escalable Host"
                className="h-10 w-auto hidden dark:block transition-all duration-300"
              />
            </div>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-4">
            <button
              onClick={() => {
                if (location.pathname === '/') {
                  window.location.reload();
                } else {
                  navigate('/');
                }
              }}
              className="px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 hover:scale-105 text-gray-700 dark:text-gray-200 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-blue-50 dark:hover:bg-slate-800"
            >
              Inicio
            </button>

            {/* Menú Servicios */}
            <div className="relative group">
              <button
                className="flex items-center space-x-1 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 hover:scale-105 text-gray-700 dark:text-gray-200 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-blue-50 dark:hover:bg-slate-800"
              >
                <span>Servicios</span>
                <ChevronDown className="w-4 h-4 transition-transform duration-200 group-hover:rotate-180" />
              </button>
              <div className="absolute top-full left-0 w-40 bg-white dark:bg-slate-800 rounded-lg shadow-lg border border-gray-100 dark:border-gray-700 py-2 hidden group-hover:block z-50">
                <button
                  onClick={() => navigate('/hostings')}
                  className="block w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-blue-50 dark:hover:bg-slate-700 hover:text-blue-600 dark:hover:text-blue-400"
                >
                  Hosting
                </button>
                <button
                  onClick={() => navigate('/complementos')}
                  className="block w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-blue-50 dark:hover:bg-slate-700 hover:text-blue-600 dark:hover:text-blue-400"
                >
                  Complementos
                </button>
              </div>
            </div>

            {isSignedIn && (
              <button
                onClick={() => navigate('/dashboard')}
                className="px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 hover:scale-105 text-gray-700 dark:text-gray-200 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-blue-50 dark:hover:bg-slate-800"
              >
                Mis Servicios
              </button>
            )}
            {isSignedIn && (
              <button
                onClick={handleProfileClick}
                className="flex items-center space-x-2 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 hover:scale-105 text-gray-700 dark:text-gray-200 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-blue-50 dark:hover:bg-slate-800"
              >
                <User className="w-4 h-4" />
                <span>Mi Perfil</span>
              </button>
            )}
            {isAdmin && (
              <div className="relative group">
                <button
                  onClick={() => navigate('/admin')}
                  className="flex items-center space-x-2 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 hover:scale-105 text-gray-700 dark:text-gray-200 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-blue-50 dark:hover:bg-slate-800"
                >
                  <Shield className="w-4 h-4" />
                  <span>Admin</span>
                </button>
                <div className="absolute top-full left-0 w-40 bg-white dark:bg-slate-800 rounded-lg shadow-lg border border-gray-100 dark:border-gray-700 py-2 hidden group-hover:block z-50">
                  <button
                    onClick={() => navigate('/admin')}
                    className="block w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-blue-50 dark:hover:bg-slate-700 hover:text-blue-600 dark:hover:text-blue-400"
                  >
                    Hostings
                  </button>
                  <button
                    onClick={() => navigate('/admin/complements')}
                    className="block w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-blue-50 dark:hover:bg-slate-700 hover:text-blue-600 dark:hover:text-blue-400"
                  >
                    Complementos
                  </button>
                  <button
                    onClick={() => navigate('/admin/orders')}
                    className="block w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-blue-50 dark:hover:bg-slate-700 hover:text-blue-600 dark:hover:text-blue-400"
                  >
                    Órdenes
                  </button>
                  <button
                    onClick={() => navigate('/admin/users')}
                    className="block w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-blue-50 dark:hover:bg-slate-700 hover:text-blue-600 dark:hover:text-blue-400"
                  >
                    Usuarios
                  </button>
                  <button
                    onClick={() => navigate('/admin/notifications')}
                    className="block w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-blue-50 dark:hover:bg-slate-700 hover:text-blue-600 dark:hover:text-blue-400"
                  >
                    Notificaciones
                  </button>
                  <button
                    onClick={() => navigate('/admin/media')}
                    className="block w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-blue-50 dark:hover:bg-slate-700 hover:text-blue-600 dark:hover:text-blue-400"
                  >
                    Archivos
                  </button>
                  <button
                    onClick={() => navigate('/admin/external-services')}
                    className="block w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-blue-50 dark:hover:bg-slate-700 hover:text-blue-600 dark:hover:text-blue-400"
                  >
                    Servicios Externos
                  </button>
                  <button
                    onClick={() => {
                      clearCache();
                      alert('Caché borrada exitosamente');
                      window.location.reload();
                    }}
                    className="block w-full text-left px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 font-medium border-t border-gray-100 dark:border-gray-700"
                  >
                    Borrar Caché
                  </button>
                  <button
                    onClick={async () => {
                      try {
                        const confirmForce = window.confirm('¿Estás seguro de forzar la ejecución del Cron de correos?');
                        if (!confirmForce) return;
                        const res = await fetch('/api/cron-check-expirations', {
                          headers: { 'x-manual-trigger': 'true' }
                        });
                        if (!res.ok) throw new Error('Error al ejecutar el cron');
                        const data = await res.json();
                        alert(`Cron ejecutado exitosamente.\nCorreos enviados: ${data.emailsSent}\nNotificaciones: ${data.notifsCreated}`);
                      } catch (err) {
                        alert('Hubo un error al forzar el cron.');
                      }
                    }}
                    className="block w-full text-left px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 font-medium border-t border-gray-100 dark:border-gray-700"
                  >
                    Forzar Notificaciones
                  </button>
                </div>
              </div>
            )}
            <div className="flex items-center space-x-3 lg:space-x-4">
              <ThemeToggle />
              <NotificationBell />
              <div className="flex items-center gap-2">
                <SignInButton />
              </div>
            </div>
          </nav>

          {/* Mobile: campana + botón menú */}
          <div className="lg:hidden flex items-center gap-1">
            <NotificationBell />
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="p-2 rounded-lg transition-colors duration-200 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-slate-800"
              aria-label={isMenuOpen ? "Cerrar menú" : "Abrir menú"}
            >
              {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <div className="lg:hidden absolute top-full left-0 right-0 bg-white dark:bg-slate-900 border-b border-gray-100 dark:border-gray-700 shadow-lg">
            <nav className="px-4 py-4 space-y-2">
              <button
                onClick={() => {
                  if (location.pathname === '/') {
                    window.location.reload();
                  } else {
                    navigate('/');
                  }
                  setIsMenuOpen(false)
                }}
                className="block w-full px-4 py-3 rounded-lg text-gray-700 dark:text-gray-200 hover:bg-blue-50 dark:hover:bg-slate-800 hover:text-blue-600 dark:hover:text-blue-400 transition-all duration-200 font-medium text-left"
              >
                Inicio
              </button>

              <div className="px-4 py-2 text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider">
                Servicios
              </div>
              <button
                onClick={() => {
                  navigate('/hostings')
                  setIsMenuOpen(false)
                }}
                className="block w-full px-4 py-2 rounded-lg text-gray-700 dark:text-gray-200 hover:bg-blue-50 dark:hover:bg-slate-800 hover:text-blue-600 dark:hover:text-blue-400 transition-all duration-200 font-medium text-left pl-6"
              >
                Hosting
              </button>
              <button
                onClick={() => {
                  navigate('/complementos')
                  setIsMenuOpen(false)
                }}
                className="block w-full px-4 py-2 rounded-lg text-gray-700 dark:text-gray-200 hover:bg-blue-50 dark:hover:bg-slate-800 hover:text-blue-600 dark:hover:text-blue-400 transition-all duration-200 font-medium text-left pl-6"
              >
                Complementos
              </button>

              {isSignedIn && (
                <button
                  onClick={() => {
                    navigate('/dashboard')
                    setIsMenuOpen(false)
                  }}
                  className="block w-full px-4 py-3 rounded-lg text-gray-700 dark:text-gray-200 hover:bg-blue-50 dark:hover:bg-slate-800 hover:text-blue-600 dark:hover:text-blue-400 transition-all duration-200 font-medium text-left"
                >
                  Mis Servicios
                </button>
              )}
              {isSignedIn && (
                <button
                  onClick={() => {
                    handleProfileClick();
                    setIsMenuOpen(false);
                  }}
                  className="flex items-center space-x-2 w-full px-4 py-3 rounded-lg text-gray-700 dark:text-gray-200 hover:bg-blue-50 dark:hover:bg-slate-800 hover:text-blue-600 dark:hover:text-blue-400 transition-all duration-200 font-medium text-left"
                >
                  <User className="w-4 h-4" />
                  <span>Mi Perfil</span>
                </button>
              )}
              <div className="px-4 py-3 space-y-6">
                <div className="flex justify-start">
                  <ThemeToggle />
                </div>
                <SignInButton />
              </div>
              {isAdmin && (
                <>
                  <div className="px-4 py-2 text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider">
                    Administración
                  </div>
                  <button
                    onClick={() => {
                      navigate('/admin');
                      setIsMenuOpen(false);
                    }}
                    className="flex items-center space-x-2 w-full px-4 py-2 rounded-lg text-gray-700 dark:text-gray-200 hover:bg-blue-50 dark:hover:bg-slate-800 hover:text-blue-600 dark:hover:text-blue-400 transition-all duration-200 font-medium text-left pl-6"
                  >
                    <span>Hostings</span>
                  </button>
                  <button
                    onClick={() => {
                      navigate('/admin/complements');
                      setIsMenuOpen(false);
                    }}
                    className="flex items-center space-x-2 w-full px-4 py-2 rounded-lg text-gray-700 dark:text-gray-200 hover:bg-blue-50 dark:hover:bg-slate-800 hover:text-blue-600 dark:hover:text-blue-400 transition-all duration-200 font-medium text-left pl-6"
                  >
                    <span>Complementos</span>
                  </button>
                  <button
                    onClick={() => {
                      navigate('/admin/orders');
                      setIsMenuOpen(false);
                    }}
                    className="flex items-center space-x-2 w-full px-4 py-2 rounded-lg text-gray-700 dark:text-gray-200 hover:bg-blue-50 dark:hover:bg-slate-800 hover:text-blue-600 dark:hover:text-blue-400 transition-all duration-200 font-medium text-left pl-6"
                  >
                    <span>Órdenes</span>
                  </button>
                  <button
                    onClick={() => {
                      navigate('/admin/users');
                      setIsMenuOpen(false);
                    }}
                    className="flex items-center space-x-2 w-full px-4 py-2 rounded-lg text-gray-700 dark:text-gray-200 hover:bg-blue-50 dark:hover:bg-slate-800 hover:text-blue-600 dark:hover:text-blue-400 transition-all duration-200 font-medium text-left pl-6"
                  >
                    <span>Usuarios</span>
                  </button>
                  <button
                    onClick={() => {
                      navigate('/admin/notifications');
                      setIsMenuOpen(false);
                    }}
                    className="flex items-center space-x-2 w-full px-4 py-2 rounded-lg text-gray-700 dark:text-gray-200 hover:bg-blue-50 dark:hover:bg-slate-800 hover:text-blue-600 dark:hover:text-blue-400 transition-all duration-200 font-medium text-left pl-6"
                  >
                    <span>Notificaciones</span>
                  </button>
                  <button
                    onClick={() => {
                      navigate('/admin/media');
                      setIsMenuOpen(false);
                    }}
                    className="flex items-center space-x-2 w-full px-4 py-2 rounded-lg text-gray-700 dark:text-gray-200 hover:bg-blue-50 dark:hover:bg-slate-800 hover:text-blue-600 dark:hover:text-blue-400 transition-all duration-200 font-medium text-left pl-6"
                  >
                    <span>Archivos</span>
                  </button>
                  <button
                    onClick={() => {
                      navigate('/admin/external-services');
                      setIsMenuOpen(false);
                    }}
                    className="flex items-center space-x-2 w-full px-4 py-2 rounded-lg text-gray-700 dark:text-gray-200 hover:bg-blue-50 dark:hover:bg-slate-800 hover:text-blue-600 dark:hover:text-blue-400 transition-all duration-200 font-medium text-left pl-6"
                  >
                    <span>Servicios Externos</span>
                  </button>
                  <button
                    onClick={() => {
                      clearCache();
                      alert('Caché borrada exitosamente');
                      window.location.reload();
                      setIsMenuOpen(false);
                    }}
                    className="flex items-center space-x-2 w-full px-4 py-2 rounded-lg text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-all duration-200 font-medium text-left pl-6"
                  >
                    <span>Borrar Caché</span>
                  </button>
                  <button
                    onClick={async () => {
                      try {
                        const confirmForce = window.confirm('¿Estás seguro de forzar la ejecución del Cron de correos?');
                        if (!confirmForce) return;
                        const res = await fetch('/api/cron-check-expirations', {
                          headers: { 'x-manual-trigger': 'true' }
                        });
                        if (!res.ok) throw new Error('Error al ejecutar el cron');
                        const data = await res.json();
                        alert(`Cron ejecutado exitosamente.\nCorreos enviados: ${data.emailsSent}\nNotificaciones: ${data.notifsCreated}`);
                      } catch (err) {
                        alert('Hubo un error al forzar el cron.');
                      }
                      setIsMenuOpen(false);
                    }}
                    className="flex items-center space-x-2 w-full px-4 py-2 rounded-lg text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-all duration-200 font-medium text-left pl-6"
                  >
                    <span>Forzar Notificaciones</span>
                  </button>
                </>
              )}
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
